import translate from '@translate';
import React, { FC, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Typography from 'src/components/Typography';
import { colors, sizes } from 'src/themes/default';
import { RootStoreType } from 'src/types/store';
import { styled } from '@mui/material/styles';
import {
    getOverviewIntervalOrdersInStatusesFetch,
    getOverviewIntervalOrdersDeliveredFetch,
} from 'src/resources/overviewInterval/reducer';
import moment from 'moment';
import Tooltip from 'src/components/Tooltip';
import Menu from 'src/components/Menu';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { InfoIcon } from 'src/components/SkladonIcon/Icons';
import {
    DeliveredSuccessBigIcon,
    OnTheWaySuccessBigIcon,
    ProcessingOkBigIcon,
    ProcessingSuccessBigIcon,
    ReceivedErrorBigIcon,
    ReceivedSuccessBigIcon,
    WaitingForCarrierSuccessBigIcon,
} from 'src/components/SkladonIcon/StatusIcons';
import { orderStatusColors } from 'src/resources/constantsCodes/constants';
import getStatusColor from 'src/utils/getStatusColor';

interface MenuOptionType {
    label: string;
    value: string;
}
const TIME_OPTIONS: MenuOptionType[] = [
    { label: translate('overview.ordersTimeOption.last24Hours'), value: 'last24Hours' },
    { label: translate('overview.ordersTimeOption.last7Days'), value: 'last7Days' },
    { label: translate('overview.ordersTimeOption.last30Days'), value: 'last30Days' },
    { label: translate('overview.ordersTimeOption.last90Days'), value: 'last90Days' },
];

const StyledContainer = styled('div')({});

const TimeBlockOption = styled('div')({
    width: '100%',
    padding: '9px 24px',
});

const InfoIconStyled = styled('span')({
    marginLeft: '10px',

    '& svg': {
        width: '17px',
        height: '17px',
        marginRight: '10px',
    },
});

const TimeRangeButton = styled('div')({
    display: 'inline-block',
    width: '90px',
    textAlign: 'left',

    '& svg': {
        width: '20px',
        height: '20px',
        position: 'absolute',
        right: '5px',
        top: '-2px',

        '& path': {
            fill: 'inherit',
            stroke: 'inherit',
        },
    },
});

const lineheight = '28px';
const StatusCountRow = styled('div')({
    '& > p': {
        lineHeight: '32px',
        verticalAlign: 'bottom',
    },
    '& svg': {
        width: lineheight,
        height: lineheight,
        marginRight: '10px',
    },

    '& .MuiTypography-body1': {},

    '& .MuiTypography-body2': {
        fontSize: '18px',
        fontWeight: 600,
        float: 'right',
        paddingRight: '10px',
    },
});

interface TextCountProps {
    status: string;
    icon: JSX.Element;
    count?: number;
}
const TextCount: FC<TextCountProps> = (props) => {
    const statusColorToken = orderStatusColors[props.status as keyof typeof orderStatusColors];
    return (
        <StatusCountRow>
            {props.icon}
            <Typography variant="body1" display="inline" sx={{ color: getStatusColor(statusColorToken) }}>
                {translate(`utils.orderStatusCodes.${props.status}`)}
            </Typography>
            <Typography variant="body2" display="inline">
                {props.count || 0}
            </Typography>
        </StatusCountRow>
    );
};

const OrdersInStatuses: FC<{ mobile?: boolean }> = (props) => {
    const dispatch = useDispatch();

    const orderInStatusesData = useSelector((state: RootStoreType) => state.overviewInterval.orderInStatusesData);
    const deliveredOrders = useSelector((state: RootStoreType) => state.overviewInterval.deliveredOrders);
    const loading = useSelector((state: RootStoreType) => state.overviewInterval.loadingOrdersInStatuses);

    const [selectOption, setSelectOption] = useState<MenuOptionType>(TIME_OPTIONS[0]);

    useEffect(() => {
        let time = moment();
        switch (selectOption.value) {
            case 'last7Days':
                time = time.subtract(6, 'd').startOf('d');
                break;
            case 'last30Days':
                time = time.subtract(29, 'd').startOf('d');
                break;
            case 'last90Days':
                time = time.subtract(89, 'd').startOf('d');
                break;
            case 'last24Hours':
            default:
                time = time.subtract(24, 'h');
        }
        dispatch(getOverviewIntervalOrdersDeliveredFetch(time.format('YYYY-MM-DD HH:mm:ss')));
    }, [selectOption]);

    useEffect(() => {
        dispatch(getOverviewIntervalOrdersInStatusesFetch());
    }, []);

    const onChangeOption = (option: MenuOptionType) => {
        setSelectOption(option);
    };

    return (
        <StyledContainer>
            <Typography variant="body1" gutterBottom component={'div'}>
                {translate('overview.ordersInStatuses.title')}
                <Tooltip title={translate('overview.ordersInStatuses.titleInfo')}>
                    <InfoIconStyled>
                        <InfoIcon />
                    </InfoIconStyled>
                </Tooltip>
            </Typography>
            {[
                { status: 'received', icon: <ReceivedSuccessBigIcon /> },
                { status: 'incorrect', icon: <ReceivedErrorBigIcon /> },
                { status: 'inProcess', icon: <ProcessingSuccessBigIcon /> },
                { status: 'packed', icon: <ProcessingOkBigIcon /> },
                { status: 'waitingForCarrier', icon: <WaitingForCarrierSuccessBigIcon /> },
                { status: 'onTheWay', icon: <OnTheWaySuccessBigIcon /> },
            ].map((item) => (
                <TextCount
                    key={item.status}
                    status={item.status}
                    count={orderInStatusesData?.find((itemCount) => itemCount.STATUS === item.status)?.count}
                    icon={item.icon}
                />
            ))}
            <Typography variant="body1" gutterBottom component={'div'}>
                {translate('overview.ordersInStatuses.titleDelivered')}
                <Tooltip title={translate('overview.ordersInStatuses.titleDeliveredInfo')}>
                    <InfoIconStyled>
                        <InfoIcon />
                    </InfoIconStyled>
                </Tooltip>
                <Menu
                    variant="menu"
                    inline
                    text={
                        <Tooltip title={translate('overview.timeRange')}>
                            <TimeRangeButton>
                                <Typography variant="body1" component="span">
                                    {selectOption.label}
                                    <ArrowDropDownIcon />
                                </Typography>
                            </TimeRangeButton>
                        </Tooltip>
                    }
                    textSx={{
                        padding: '0',
                        marginRight: props.mobile ? 0 : '20px',
                        backgroundColor: colors.background_W,
                        '&:hover': { backgroundColor: colors.background_W },
                        '&:active': { backgroundColor: colors.background_W },
                        '&:active:hover': { backgroundColor: colors.background_W },
                    }}
                    sx={{ '&  li': { minHeight: 'auto', padding: 0 } }}
                    items={TIME_OPTIONS.map((option) => (
                        <TimeBlockOption key={option.value} onClick={() => onChangeOption(option)}>
                            {translate(`${option.label}`)}
                        </TimeBlockOption>
                    ))}
                />
            </Typography>
            <TextCount status={'delivered'} count={deliveredOrders} icon={<DeliveredSuccessBigIcon />} />
        </StyledContainer>
    );
};

export default OrdersInStatuses;
